import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html, Sky, Stars, Environment, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';

/**
 * Towers of Mastery — three themed 3D towers.
 * Spiral staircase: 12 steps per revolution (3 per wall × 4 walls).
 * Each answered question advances one step, camera orbits +15° and rises.
 */

const STEPS_PER_REV = 12;
const STEP_HEIGHT = 0.55;
const TOWER_RADIUS = 2.4;
const STAIR_RADIUS = TOWER_RADIUS + 0.55;
const CAMERA_DISTANCE = 8;

const TOWERS = {
  english: {
    label: 'English Kingdom',
    banner: 'Gold',
    accent: '#E8B84A',
    stone: '#F5E9CB',
    accent2: '#FFF3C4',
    sky: '#F6D67F',
    subtitle: 'Cathedral of Words',
  },
  math: {
    label: 'Math Mountains',
    banner: 'Red',
    accent: '#B22222',
    stone: '#2A2A2A',
    accent2: '#4A0F0F',
    sky: '#3A1010',
    subtitle: 'Fortress of Numbers',
  },
  science: {
    label: 'Science Swamp',
    banner: 'Purple & Green',
    accent: '#8A56C1',
    stone: '#E9E0D4',
    accent2: '#4CBB6C',
    sky: '#2B3C4A',
    subtitle: 'Alembic Spire',
  },
};

// ----- Tower geometry -----
function TowerBody({ theme, height = 30 }) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, -0.3, 0]} receiveShadow>
        <cylinderGeometry args={[TOWER_RADIUS + 0.9, TOWER_RADIUS + 1.4, 0.6, 32]} />
        <meshStandardMaterial color={theme.accent2} roughness={0.8} />
      </mesh>
      {/* Main shaft — a square-ish tower using cylinder for round castle */}
      <mesh position={[0, height / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[TOWER_RADIUS, TOWER_RADIUS, height, 8]} />
        <meshStandardMaterial color={theme.stone} roughness={0.75} metalness={0.05} />
      </mesh>
      {/* Battlements at top */}
      <mesh position={[0, height + 0.3, 0]}>
        <cylinderGeometry args={[TOWER_RADIUS + 0.15, TOWER_RADIUS + 0.15, 0.6, 8]} />
        <meshStandardMaterial color={theme.accent} roughness={0.6} />
      </mesh>
      {/* Roof cone */}
      <mesh position={[0, height + 1.5, 0]}>
        <coneGeometry args={[TOWER_RADIUS + 0.2, 2, 8]} />
        <meshStandardMaterial color={theme.accent} roughness={0.5} metalness={0.2} />
      </mesh>
      {/* Flag pole + flag */}
      <mesh position={[0, height + 2.9, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 1.6, 6]} />
        <meshStandardMaterial color="#111" />
      </mesh>
      <mesh position={[0.5, height + 3.3, 0]}>
        <planeGeometry args={[1, 0.6]} />
        <meshStandardMaterial color={theme.accent} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// A single stair step at (angle, index)
function StairStep({ level, isCurrent, isCompleted, accent }) {
  const angle = (level * (2 * Math.PI)) / STEPS_PER_REV;
  const x = STAIR_RADIUS * Math.cos(angle);
  const z = STAIR_RADIUS * Math.sin(angle);
  const y = level * STEP_HEIGHT;
  const meshRef = useRef();

  useFrame(() => {
    if (!meshRef.current) return;
    if (isCurrent) {
      meshRef.current.material.emissiveIntensity = 0.9 + Math.sin(Date.now() / 200) * 0.3;
    } else if (isCompleted) {
      meshRef.current.material.emissiveIntensity = 0.35;
    } else {
      meshRef.current.material.emissiveIntensity = 0;
    }
  });

  return (
    <mesh ref={meshRef} position={[x, y + 0.05, z]}
      rotation={[0, -angle + Math.PI / 2, 0]} castShadow>
      <boxGeometry args={[0.9, 0.1, 0.5]} />
      <meshStandardMaterial
        color={isCurrent ? '#FFFFFF' : isCompleted ? accent : '#7A6E52'}
        emissive={accent}
        emissiveIntensity={0}
        roughness={0.4}
      />
    </mesh>
  );
}

// User avatar — small glowing capsule with a name floating above.
function Avatar({ targetLevel, accent, name = 'You', rebirths = 0 }) {
  const ref = useRef();
  const currentLevel = useRef(0);

  useFrame(() => {
    if (!ref.current) return;
    // Smoothly lerp position toward target level
    currentLevel.current += (targetLevel - currentLevel.current) * 0.08;
    const angle = (currentLevel.current * (2 * Math.PI)) / STEPS_PER_REV;
    ref.current.position.x = STAIR_RADIUS * Math.cos(angle);
    ref.current.position.z = STAIR_RADIUS * Math.sin(angle);
    ref.current.position.y = currentLevel.current * STEP_HEIGHT + 0.55;
    ref.current.rotation.y = -angle + Math.PI / 2 - Math.PI / 8;
  });

  return (
    <group ref={ref}>
      <mesh castShadow>
        <capsuleGeometry args={[0.18, 0.35, 6, 12]} />
        <meshStandardMaterial color="#EEE" emissive={accent} emissiveIntensity={0.6} />
      </mesh>
      <mesh position={[0, 0.42, 0]}>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshStandardMaterial color="#F5D6B4" />
      </mesh>
      {/* Rebirth crown */}
      {rebirths > 0 && (
        <mesh position={[0, 0.62, 0]}>
          <coneGeometry args={[0.14, 0.16, 6]} />
          <meshStandardMaterial color="#FFD84D" emissive="#FFD84D" emissiveIntensity={0.7} />
        </mesh>
      )}
      <Html center distanceFactor={12} position={[0, 0.9, 0]} style={{ pointerEvents: 'none' }}>
        <div style={{
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          padding: '2px 8px', borderRadius: 8, fontFamily: 'Playfair Display, serif',
          fontSize: 12, whiteSpace: 'nowrap', border: `1px solid ${accent}`,
        }}>
          {name}{rebirths > 0 ? ` · ${rebirths}★` : ''}
        </div>
      </Html>
    </group>
  );
}

// Orbiting camera controller
function CameraRig({ targetLevel, targetAngleDeg }) {
  const startingLevel = useRef(0);
  useFrame(({ camera }) => {
    // Smoothly track the avatar's Y position
    startingLevel.current += (targetLevel - startingLevel.current) * 0.06;
    const avatarY = startingLevel.current * STEP_HEIGHT + 0.55;
    const angle = (targetAngleDeg * Math.PI) / 180;
    // Position camera on an arc from top-right of tower, slightly above avatar
    const camX = Math.cos(angle) * CAMERA_DISTANCE;
    const camZ = Math.sin(angle) * CAMERA_DISTANCE;
    const camY = avatarY + 3.5;
    camera.position.lerp(new THREE.Vector3(camX, camY, camZ), 0.06);
    camera.lookAt(0, avatarY, 0);
  });
  return null;
}

// Sparkles when correct
function CorrectSparkles({ triggerKey, accent }) {
  const groupRef = useRef();
  const start = useRef(0);
  useEffect(() => { start.current = Date.now(); }, [triggerKey]);
  useFrame(() => {
    if (!groupRef.current) return;
    const t = (Date.now() - start.current) / 1000;
    groupRef.current.children.forEach((c, i) => {
      c.position.y = 0.6 + t * 2 + Math.sin(i) * 0.3;
      c.material.opacity = Math.max(0, 1 - t / 1.5);
    });
  });
  return (
    <group ref={groupRef} key={triggerKey}>
      {[...Array(12)].map((_, i) => (
        <mesh key={i} position={[Math.cos(i) * 0.3, 0.6, Math.sin(i) * 0.3]}>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshBasicMaterial color={accent} transparent opacity={1} />
        </mesh>
      ))}
    </group>
  );
}

function TowerScene({ tower, progress, correctKey, cameraAngle }) {
  const theme = TOWERS[tower];
  const totalStepsToRender = 60; // show a window of stairs
  const centerLevel = Math.max(0, progress.level - 20);

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 30, 10]} intensity={1.1} castShadow />
      <hemisphereLight args={[theme.sky, '#2b2b2b', 0.5]} />

      {tower === 'math' ? <Stars radius={100} depth={50} count={2000} factor={4} fade /> : null}
      {tower === 'english' ? <Sky sunPosition={[10, 20, 10]} turbidity={2} rayleigh={1} /> : null}
      {tower === 'science' ? (
        <>
          {/* Purple/green fog cloud */}
          <mesh position={[0, 5, -10]} scale={[20, 12, 1]}>
            <planeGeometry />
            <meshBasicMaterial color="#3a1e5a" transparent opacity={0.5} />
          </mesh>
        </>
      ) : null}

      <TowerBody theme={theme} height={40} />

      {[...Array(totalStepsToRender)].map((_, i) => {
        const level = centerLevel + i;
        return (
          <StairStep key={level} level={level}
            isCurrent={level === progress.level}
            isCompleted={level < progress.level}
            accent={theme.accent} />
        );
      })}

      <Avatar targetLevel={progress.level} accent={theme.accent}
              name="You" rebirths={progress.rebirths} />

      {/* Sparkles anchored at avatar position */}
      <group position={[
        STAIR_RADIUS * Math.cos((progress.level * 2 * Math.PI) / STEPS_PER_REV),
        progress.level * STEP_HEIGHT,
        STAIR_RADIUS * Math.sin((progress.level * 2 * Math.PI) / STEPS_PER_REV),
      ]}>
        <CorrectSparkles triggerKey={correctKey} accent={theme.accent} />
      </group>

      <CameraRig targetLevel={progress.level} targetAngleDeg={cameraAngle} />

      {/* Ground */}
      <mesh position={[0, -0.7, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[30, 32]} />
        <meshStandardMaterial color={tower === 'science' ? '#2a5a3a' : tower === 'math' ? '#1a1a1a' : '#c9a86a'} />
      </mesh>
    </>
  );
}

// ----- Question overlay -----
function QuestionCard({ q, onAnswer, feedback }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => { setSelected(null); }, [q?.id]);

  if (!q) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="parchment rounded-2xl p-5 max-w-md w-[92vw]" data-testid="tower-question-card">
      <div className="font-body-story italic text-xs text-[#68615A]">
        {q.subject} · {q.topic || 'General'} · difficulty {q.difficulty} · level {q.current_level} / {q.max_level}
      </div>
      <div className="font-storybook text-lg mt-1 mb-3 text-[#2C2621]">{q.prompt}</div>
      <div className="grid gap-2">
        {Object.entries(q.choices).map(([letter, text]) => {
          const isSel = selected === letter;
          return (
            <button
              key={letter}
              data-testid={`tower-choice-${letter}`}
              disabled={!!feedback}
              onClick={() => { setSelected(letter); onAnswer(letter); }}
              className={`text-left px-4 py-2.5 rounded-xl border-2 transition-colors ${
                isSel
                  ? (feedback?.correct ? 'border-[#2B5329] bg-[#2B5329]/15' :
                     feedback ? 'border-[#8C1C13] bg-[#8C1C13]/15' :
                     'border-[#8C1C13] bg-[#8C1C13]/10')
                  : 'border-[#C9B99A] hover:border-[#8C1C13]/50'
              }`}
            >
              <span className="inline-block w-6 font-bold text-[#8C1C13] font-storybook">{letter}.</span>
              {text}
            </button>
          );
        })}
      </div>
      {feedback && (
        <div data-testid="tower-feedback" className={`mt-3 text-sm font-body-story ${
          feedback.correct ? 'text-[#2B5329]' : 'text-[#8C1C13]'
        }`}>
          {feedback.correct ? 'Correct! Ascend the stairs.' : feedback.explanation || 'Try again — no advance.'}
        </div>
      )}
    </motion.div>
  );
}

// ----- Main page -----
export default function TowersOfMastery() {
  const { API, user, mode } = useApp();
  const nav = useNavigate();
  const [selectedTower, setSelectedTower] = useState(() => localStorage.getItem('pp-tower') || 'english');
  const [progress, setProgress] = useState({ english: null, math: null, science: null });
  const [question, setQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [correctKey, setCorrectKey] = useState(0);
  const [cameraAngle, setCameraAngle] = useState(35);
  const [toast, setToast] = useState(null);

  useEffect(() => { if (mode !== 'storybook') nav('/'); }, [mode]);
  useEffect(() => { localStorage.setItem('pp-tower', selectedTower); }, [selectedTower]);

  const token = () => localStorage.getItem('pp-token') || '';

  const loadProgress = async () => {
    if (!user) return;
    const r = await axios.get(`${API}/tower/progress`, { headers: { Authorization: `Bearer ${token()}` } });
    setProgress(r.data);
  };
  const loadQuestion = async () => {
    if (!user) return;
    setFeedback(null);
    try {
      const r = await axios.get(`${API}/tower/${selectedTower}/question`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setQuestion(r.data);
    } catch { setQuestion(null); }
  };

  useEffect(() => { loadProgress(); loadQuestion(); }, [user, selectedTower]);

  const answer = async (letter) => {
    if (!question) return;
    const r = await axios.post(`${API}/tower/answer`,
      { subject: selectedTower, question_id: question.id, answer: letter },
      { headers: { Authorization: `Bearer ${token()}` } });
    setFeedback({ correct: r.data.correct, explanation: r.data.explanation });
    if (r.data.correct) {
      setCorrectKey(k => k + 1);
      setCameraAngle(a => (a + 15) % 360);
      setProgress(p => ({ ...p, [selectedTower]: {
        ...(p[selectedTower] || {}),
        level: r.data.level, xp: r.data.xp, streak: r.data.streak, rebirths: r.data.rebirths,
      }}));
      if (r.data.checkpoint) setToast({ kind: 'flag', text: `Checkpoint reached at level ${r.data.level}!` });
      if (r.data.boss_unlocked) setToast({ kind: 'boss', text: `Boss challenge unlocked at level ${r.data.level}!` });
      if (r.data.reached_top) setToast({ kind: 'rebirth', text: `Tower conquered! +1 Rebirth in ${selectedTower.toUpperCase()}.` });
      // Load the next question after animation
      setTimeout(() => { loadQuestion(); loadProgress(); }, 1400);
    }
  };

  if (mode !== 'storybook') return null;
  const p = progress[selectedTower] || { level: 0, xp: 0, streak: 0, rebirths: 0, accuracy: 0, max_level: 500 };
  const theme = TOWERS[selectedTower];

  return (
    <div className="min-h-screen relative">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-6 py-4">
        <button data-testid="tower-back" onClick={() => nav('/')}
          className="storybook-btn px-4 py-2 rounded-full text-xs">← Back to Kingdom</button>
        <div className="storybook-btn px-4 py-2 rounded-full text-xs font-storybook">
          Towers of Mastery
        </div>
        <ModeToggle />
      </div>

      {/* Tower picker */}
      <div className="fixed top-20 left-6 z-30 flex flex-col gap-2 max-w-[220px]">
        {Object.entries(TOWERS).map(([key, t]) => {
          const pp = progress[key] || { level: 0, rebirths: 0 };
          const active = key === selectedTower;
          return (
            <button key={key} data-testid={`tower-pick-${key}`}
              onClick={() => setSelectedTower(key)}
              className={`text-left rounded-xl px-3 py-2 border-2 ${
                active ? 'bg-[#FDF7E6] border-[#8C1C13]' : 'bg-[#FDF7E6]/70 border-[#C9B99A]'
              }`}>
              <div className="font-storybook text-sm text-[#2C2621]">{t.label}</div>
              <div className="text-[10px] font-body-story italic text-[#68615A]">
                {t.banner} banner · level {pp.level}/{p.max_level}{pp.rebirths ? ` · ${pp.rebirths}★` : ''}
              </div>
            </button>
          );
        })}
      </div>

      {/* Stats HUD */}
      <div className="fixed top-20 right-6 z-30 parchment rounded-xl px-4 py-3 max-w-[240px]" data-testid="tower-hud">
        <div className="font-storybook text-lg text-[#2C2621]">{theme.label}</div>
        <div className="font-body-story italic text-xs text-[#68615A]">{theme.subtitle}</div>
        <div className="mt-2 grid grid-cols-2 gap-1 text-xs font-body-story text-[#2C2621]">
          <div>Level: <b data-testid="tower-level">{p.level}</b>/{p.max_level}</div>
          <div>XP: <b>{p.xp}</b></div>
          <div>Streak: <b>{p.streak}</b></div>
          <div>Rebirths: <b data-testid="tower-rebirths">{p.rebirths}</b></div>
          <div className="col-span-2">Accuracy: <b>{p.accuracy}%</b></div>
        </div>
      </div>

      {/* 3D canvas */}
      <div className="absolute inset-0" style={{
        background: selectedTower === 'math'
          ? 'radial-gradient(circle at 60% 40%, #3a1010 0%, #0e0505 80%)'
          : selectedTower === 'science'
          ? 'radial-gradient(circle at 60% 40%, #4a3a6a 0%, #1c2530 80%)'
          : 'radial-gradient(circle at 60% 40%, #ffe9a8 0%, #d8a94d 80%)',
      }}>
        <Canvas shadows camera={{ position: [7, 6, 7], fov: 45 }}>
          <TowerScene tower={selectedTower} progress={p}
                      correctKey={correctKey} cameraAngle={cameraAngle} />
        </Canvas>
      </div>

      {/* Question card */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30">
        {question ? (
          <QuestionCard q={question} onAnswer={answer} feedback={feedback} />
        ) : (
          <div className="parchment rounded-xl px-4 py-3 text-sm">Loading quest…</div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }} onAnimationComplete={() => setTimeout(() => setToast(null), 2500)}
            data-testid={`tower-toast-${toast.kind}`}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-40 parchment px-6 py-3 rounded-full font-storybook text-[#8C1C13]"
          >
            {toast.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
